import React from 'react';
import {
  EAutocompleteCategoryDefinitionProps,
  EAutocompleteOption,
} from 'webapp-lib/Components';

import casusBackend, {CasusSearchItem} from '@lexian/api/casus-api';
import i18n from '_/i18n';
import {CasusFlags} from '_/sharedComponents/CasusFlags';
import {SearchCasusFilters} from '_/types/casus';
import {handlePaginatedLoadableFetch} from '_/utilities/simple-fetch';
import {
  PageItem,
  PaginatedLoadable,
  PaginationArguments,
} from '_/utilities/types';

import {
  CasusStateFilter,
  MinimalCasusSearchItem,
} from './CasusAutocomplete.types';

export const buildSearchCasus = (
  paginationArguments: Omit<PaginationArguments, 'cursor'>,
  currentLoadable: PaginatedLoadable<PageItem<CasusSearchItem>[]>,
  {filters, searchQuery}: {filters: SearchCasusFilters; searchQuery: string}
): Promise<PaginatedLoadable<PageItem<CasusSearchItem>[]>> =>
  handlePaginatedLoadableFetch(
    {
      endpointFunction: casusBackend.searchAllCasus,
      otherParams: {...filters, query: searchQuery},
      getPageItemFromResponse: (body) => body.casus,
    },
    paginationArguments,
    currentLoadable
  );

export enum CasusCategoryNames {
  RECOMMENDATIONS = 'RECOMMENDATIONS',
  FAVORITES = 'FAVORITES',
  OTHERS = 'OTHERS',
}

export const CASUS_CATEGORIES: EAutocompleteCategoryDefinitionProps[] = [
  {
    name: CasusCategoryNames.RECOMMENDATIONS,
    mainLabel: i18n.t('common:casusAutocomplete.categories.recommendations'),
  },
  {
    name: CasusCategoryNames.FAVORITES,
    mainLabel: i18n.t('common:casusAutocomplete.categories.favorites'),
  },
  {
    name: CasusCategoryNames.OTHERS,
    mainLabel: i18n.t('common:casusAutocomplete.categories.others'),
  },
];

const isAssignable = (casus: {readonly: boolean}): boolean => !casus.readonly;

export const mapStateFilter = (stateFilter: CasusStateFilter[]): boolean =>
  stateFilter.includes('ACTIVE');

export const mapToOption = (
  casus: MinimalCasusSearchItem,
  disableUnassignableCasus: boolean
): EAutocompleteOption => ({
  displayText: casus.displayNameWithClient,
  value: casus.id,
  subtext: i18n.t('common:casusNumber', {casusNumber: casus.casusNumber}),
  categoryName: casus.isFavorite
    ? CasusCategoryNames.FAVORITES
    : CasusCategoryNames.OTHERS,
  endAdornment: (
    <CasusFlags
      isInternal={casus.internal}
      isLocked={casus.locked}
      isProtected={casus.protected}
      state={casus.state}
    />
  ),
  disabled: disableUnassignableCasus && !isAssignable(casus),
});
